// Ported from microsites/womens-conference/src/components/MealsProvider.tsx; the menu is built from MP (lib/views.ts buildMeals).
import { createContext, useContext, useMemo } from 'react';
import type { Meals } from '../lib/views';

/**
 * THE LUNCH MENU, ONCE, FOR EVERYBODY WHO RENDERS IT
 * ==================================================
 *
 * Seven places on this site draw the same six lunches: the picker, the
 * schedule's lunch row, the profile, the preview thumbnails, the "their
 * conference" recap. Every one of them reached into `what-matters.ts` for
 * itself.
 *
 * That is why this is a context rather than seven props threaded down through
 * `Schedule` and `ProfileHub`. Not for tidiness — for the failure mode. A prop
 * added to six call sites and missed on the seventh does not break: that one
 * component quietly keeps rendering the config while the rest show the
 * database, and the two agree until the day somebody edits the menu. Which is
 * exactly the bug this whole change exists to remove, reintroduced by the fix.
 *
 * THE DEFAULT IS THE CONFIG, so a component rendered outside the provider
 * behaves precisely as it did before. That makes the migration safe in the
 * other direction too: nothing has to be wrapped in one go, and nothing renders
 * an empty menu because a provider was forgotten.
 */

export type { Meals };

const FALLBACK: Meals = { options: [], formFieldId: null };

const MealsContext = createContext<Meals>(FALLBACK);

export function MealsProvider({
  meals,
  children,
}: {
  /** From lib/views buildMeals: MP's answers dressed with the config's photos. */
  meals: Meals | null;
  children: React.ReactNode;
}) {
  // An empty options array is not a menu; it is a read that found nothing, and
  // it should send everybody to the config rather than render a picker with no
  // choices in it.
  const value = useMemo(() => (meals?.options.length ? meals : FALLBACK), [meals]);
  return <MealsContext.Provider value={value}>{children}</MealsContext.Provider>;
}

export function useMeals(): Meals {
  return useContext(MealsContext);
}
